import { useState, useEffect, lazy, Suspense } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "@/assets/logo.webp";
import {
  Moon,
  Sun,
  Menu,
  X,
  Home,
  Umbrella,
  Mountain,
  UtensilsCrossed,
  Music,
  Map as MapIcon,
  Waves,
  CloudSun,
  Cloud,
  Newspaper,
  Calendar,
  Info,
  Mail,
  BookOpen,
  Landmark,
  Search,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useIsMobile } from "@/hooks/use-mobile";

const SiteSearch = lazy(() => import("@/components/SiteSearch"));

type NavLinkItem = { label: string; to: string; icon: React.ComponentType<{ className?: string }> };

const linkGroups: { heading: string; items: NavLinkItem[] }[] = [
  {
    heading: "Explore",
    items: [
      { label: "Home", to: "/", icon: Home },
      { label: "Today", to: "/today", icon: Sun },
      { label: "Right Now", to: "/right-now", icon: Sun },
      { label: "Beaches", to: "/beaches", icon: Umbrella },
      { label: "Trails", to: "/trails", icon: Mountain },
      { label: "Map", to: "/map", icon: MapIcon },
    ],
  },
  {
    heading: "Eat & Play",
    items: [
      { label: "Dining", to: "/dining", icon: UtensilsCrossed },
      { label: "Nightlife", to: "/nightlife", icon: Music },
      { label: "Culture", to: "/culture", icon: Landmark },
      { label: "Events", to: "/events", icon: Calendar },
    ],
  },
  {
    heading: "Conditions",
    items: [
      { label: "Surf", to: "/surf", icon: Waves },
      { label: "Tides", to: "/tides", icon: Cloud },
      { label: "Weather", to: "/weather", icon: CloudSun },
    ],
  },
  {
    heading: "Stories",
    items: [
      { label: "Blog", to: "/blog", icon: Newspaper },
      { label: "Guides", to: "/guides", icon: BookOpen },
      { label: "Nature Calendar", to: "/nature-calendar", icon: Calendar },
      { label: "Lagoons", to: "/guides/coastal-lagoons", icon: Waves },
    ],
  },
  {
    heading: "About",
    items: [
      { label: "About", to: "/about", icon: Info },
      { label: "Contact", to: "/contact", icon: Mail },
    ],
  },
];

// Flat list for desktop nav (excludes About/Contact which live in footer)
const links = linkGroups
  .flatMap((g) => g.items)
  .filter((l) => !["/about", "/contact", "/nightlife"].includes(l.to));

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchMounted, setSearchMounted] = useState(false);
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const isMobile = useIsMobile();
  const { pathname } = useLocation();

  useEffect(() => setMounted(true), []);

  // Global search shortcuts: Cmd/Ctrl+K anywhere, "/" outside of inputs.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        !!target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        openSearch();
      } else if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const openSearch = () => {
    setSearchMounted(true);
    setSearchOpen(true);
    setMenuOpen(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Close menu on resize to desktop
  useEffect(() => {
    if (!isMobile) setMenuOpen(false);
  }, [isMobile]);

  // Treat the home page as transparent (over hero); inner pages always have solid bg.
  const isHome = pathname === "/";
  const solid = scrolled || menuOpen || !isHome;

  const isActive = (to: string) =>
    to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(to + "/");

  return (
    <nav
      className={`fixed top-0 left-0 z-50 w-full transition-all duration-500 ${
        solid
          ? "bg-[hsl(var(--header-bg)/0.75)] backdrop-blur-xl shadow-sm border-b border-[hsl(var(--header-border)/0.6)]"
          : "bg-black/30 backdrop-blur-[2px]"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-4 gap-4 lg:max-w-none lg:mx-0 lg:pl-4 lg:pr-6">
        <Link to="/" className="flex-shrink min-w-0 flex items-center gap-2">
          <img
            src={logo}
            alt="San Diego Coastal Guide logo — return to homepage"
            width={128}
            height={128}
            decoding="async"
            className="h-12 md:h-[60px] w-auto rounded-md object-contain flex-shrink-0"
          />
          <span
            className={`font-display text-sm sm:text-lg lg:text-sm xl:text-lg font-semibold transition-colors leading-tight truncate ${
              solid ? "text-foreground" : "text-accent-foreground"

            }`}
          >
            San Diego Coastal Guide
          </span>
        </Link>


        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-2 min-w-0">
          <ul className="flex items-center gap-1.5 xl:gap-3 2xl:gap-5">
            {links.map((link) => {
              const active = isActive(link.to);
              return (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className={`relative font-body text-[9px] xl:text-[11px] 2xl:text-sm font-medium uppercase tracking-[0.02em] xl:tracking-[0.04em] transition-colors whitespace-nowrap pb-1 after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-[2px] after:rounded-full after:bg-primary after:transition-transform after:duration-300 after:origin-center ${
                      active
                        ? solid
                          ? "text-primary font-semibold after:scale-x-100"
                          : "text-accent-foreground font-semibold after:scale-x-100 after:bg-accent-foreground"
                        : solid
                          ? "text-muted-foreground hover:text-foreground after:scale-x-0"
                          : "text-accent-foreground hover:bg-accent-foreground/10 after:scale-x-0"
                    }`}
                  >
                    {link.label}
                  </Link>
                </li>
              );
            })}
          </ul>

          <button
            onClick={openSearch}
            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
              solid
                ? "text-muted-foreground hover:text-foreground hover:bg-secondary"
                : "text-accent-foreground hover:bg-accent-foreground/10"
            }`}
            aria-label="Search the site"
          >
            <Search className="h-4 w-4" />
          </button>

          {mounted && (
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
                solid
                  ? "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  : "text-accent-foreground hover:bg-accent-foreground/10"
              }`}
              aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          )}
        </div>

        {/* Mobile hamburger */}
        <div className="flex lg:hidden items-center gap-3">
          <button
            onClick={openSearch}
            className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
              solid
                ? "text-muted-foreground hover:text-foreground hover:bg-secondary"
                : "text-accent-foreground hover:bg-accent-foreground/10"
            }`}
            aria-label="Search the site"
          >
            <Search className="h-5 w-5" />
          </button>

          {mounted && (
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
                solid
                  ? "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  : "text-accent-foreground hover:bg-accent-foreground/10"
              }`}
              aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          )}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
              solid
                ? "text-foreground hover:bg-secondary"
                : "text-accent-foreground hover:bg-accent-foreground/10"
            }`}
            aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-navigation-menu"
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden border-t border-border/50 bg-background/95 backdrop-blur-md max-h-[80vh] overflow-y-auto">
          <div className="px-5 py-4 space-y-5">
            {linkGroups.map((group) => (
              <div key={group.heading}>
                <h3 className="px-2 mb-2 font-body text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  {group.heading}
                </h3>
                <ul className="grid grid-cols-2 gap-1.5">
                  {group.items.map((link) => {
                    const active = isActive(link.to);
                    const Icon = link.icon;
                    return (
                      <li key={link.to}>
                        <Link
                          to={link.to}
                          className={`flex items-center gap-2.5 rounded-lg px-3 py-2.5 font-body text-sm font-medium transition-colors ${
                            active
                              ? "bg-primary/10 text-primary"
                              : "text-foreground/80 hover:bg-secondary hover:text-foreground"
                          }`}
                        >
                          <Icon
                            className={`h-4 w-4 flex-shrink-0 ${
                              active ? "text-primary" : "text-muted-foreground"
                            }`}
                          />
                          <span className="truncate">{link.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {searchMounted && (
        <Suspense fallback={null}>
          <SiteSearch open={searchOpen} onOpenChange={setSearchOpen} />
        </Suspense>
      )}
    </nav>
  );
};

export default Navbar;
